/**
 * Investor reports: what the CEO types each month, and everything the system works out from it.
 * Nothing here reads platform data — every number traces back to an entry the CEO made.
 */

export type Period = `m${number}` | 'q1' | 'q2' | 'q3' | 'q4' | 'h1' | 'h2' | 'annual';
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_SHORT = MONTHS.map((m) => m.slice(0, 3));

export interface Line { label: string; amount: number; note?: string }
export interface Row { cells: string[] }
export interface MonthEntry {
  revenue: number;
  signups: number;
  payouts: number;
  expenses: Line[];
  /** Extra headline numbers the CEO wants on the report, e.g. "Active traders". */
  metrics?: { label: string; value: string }[];
  priorities?: { priority: string; outcome: string }[];
  sections: Partial<Record<SectionKey, string>>;
}
export interface Commentary { sections: Partial<Record<SectionKey, string>>; priorities?: { priority: string; outcome: string }[] }

export const SECTIONS = [
  { key: 'summary', title: 'Executive Summary', help: 'What happened this month in a few sentences.', prospect: true },
  { key: 'growth', title: 'Revenue & Growth', help: 'Optional note. The table and charts are made from your numbers.', prospect: true, auto: true },
  { key: 'finance', title: 'Financial Breakdown', help: 'Optional note. The expense table, pie chart and surplus are calculated.', prospect: false, auto: true },
  { key: 'product', title: 'Product & Technology', help: 'Platform, CK Trader, new features.', prospect: false },
  { key: 'platform', title: 'Platform & Partners', help: 'Payment, technology and business partners.', prospect: false },
  { key: 'regulatory', title: 'Regulatory & Licensing', help: 'Licences, sandbox, legal.', prospect: false },
  { key: 'capital', title: 'Capital, Fundraising & Cap Table', help: 'Funding pipeline and ownership. Use “a | b | c” lines for a table.', prospect: false },
  { key: 'team', title: 'Team & Governance', help: 'Hires, directors, advisors, governance.', prospect: false },
  { key: 'payouts', title: 'Payout Activity & Market Trust', help: 'Optional note. The payout table is calculated.', prospect: false, auto: true },
  { key: 'review', title: 'Year to Date', help: 'Optional note. Totals since January are calculated.', prospect: false, auto: true },
  { key: 'outlook', title: 'Priorities & Outlook', help: 'What comes next. Add priorities below.', prospect: true },
  { key: 'closing', title: 'Summary', help: 'Closing note, signed by you.', prospect: true },
] as const;
export type SectionKey = (typeof SECTIONS)[number]['key'];

export const PERIODS: { id: Period; label: string; short: string; months: number[] }[] = [
  ...MONTHS.map((m, i) => ({ id: `m${i + 1}` as Period, label: m, short: MONTH_SHORT[i]!, months: [i + 1] })),
  { id: 'q1', label: 'Q1 (First Quarter)', short: 'Q1', months: [1, 2, 3] },
  { id: 'q2', label: 'Q2 (Second Quarter)', short: 'Q2', months: [4, 5, 6] },
  { id: 'q3', label: 'Q3 (Third Quarter)', short: 'Q3', months: [7, 8, 9] },
  { id: 'q4', label: 'Q4 (Fourth Quarter)', short: 'Q4', months: [10, 11, 12] },
  { id: 'h1', label: 'H1 (First Half)', short: 'H1', months: [1, 2, 3, 4, 5, 6] },
  { id: 'h2', label: 'H2 (Second Half)', short: 'H2', months: [7, 8, 9, 10, 11, 12] },
  { id: 'annual', label: 'Annual Report', short: 'Annual', months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
];
export const periodOf = (p: string) => PERIODS.find((x) => x.id === p);
/** The folders investors see for every year, in this order. */
export const FOLDERS: { id: string; label: string; periods: Period[] }[] = [
  { id: 'q4', label: 'Q4 (Fourth Quarter)', periods: ['m10', 'm11', 'm12', 'q4'] },
  { id: 'q3', label: 'Q3 (Third Quarter)', periods: ['m7', 'm8', 'm9', 'q3'] },
  { id: 'q2', label: 'Q2 (Second Quarter)', periods: ['m4', 'm5', 'm6', 'q2'] },
  { id: 'q1', label: 'Q1 (First Quarter)', periods: ['m1', 'm2', 'm3', 'q1'] },
  { id: 'halves', label: 'H1 & H2 halves', periods: ['h1', 'h2'] },
  { id: 'annual', label: 'Annual Report', periods: ['annual'] },
];
/** Which roll-ups a month completes: March → Q1, June → Q2 and H1, December → Q4, H2 and the annual report. */
export const ROLLUPS: Record<number, Period[]> = { 3: ['q1'], 6: ['q2', 'h1'], 9: ['q3'], 12: ['q4', 'h2', 'annual'] };

const n = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : Number(x) || 0);
export const pct = (now: number, before: number) => (before > 0 ? ((now - before) / before) * 100 : null);
export const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
export const money = (v: number) => `${v < 0 ? '−' : ''}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;
export const signed = (v: number | null, d = 1) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(d)}%`);
export const count = (v: number) => Math.round(v).toLocaleString('en-US');

export interface MonthCalc { month: number; label: string; revenue: number; signups: number; payouts: number; opex: number; surplus: number; revenueMoM: number | null; signupsMoM: number | null; expenses: Line[] }
export interface Calc {
  period: Period; year: number; label: string; title: string;
  months: MonthCalc[];
  revenue: number; signups: number; payouts: number; opex: number; surplus: number;
  revenueGrowth: number | null; signupsGrowth: number | null;
  avgRevenue: number; bestMonth: MonthCalc | null;
  ytd: { revenue: number; signups: number; payouts: number; growthSinceJan: number | null; months: number };
  expensesByLabel: Line[];
  tiles: { label: string; value: string; tone: 'gold' | 'dark' | 'up' | 'down' | 'brown' }[];
  missing: number[];
  warnings: string[];
}

/** Clean one month's entry so the maths never meets a string or a blank. */
export function cleanMonth(e: Partial<MonthEntry> | null | undefined): MonthEntry {
  return {
    revenue: n(e?.revenue), signups: n(e?.signups), payouts: n(e?.payouts),
    expenses: (e?.expenses ?? []).filter((x) => x && (x.label || x.amount)).map((x) => ({ label: String(x.label ?? '').trim() || 'Other', amount: n(x.amount), note: x.note ? String(x.note) : undefined })),
    metrics: (e?.metrics ?? []).filter((x) => x && x.label).map((x) => ({ label: String(x.label), value: String(x.value ?? '') })),
    priorities: (e?.priorities ?? []).filter((x) => x && x.priority).map((x) => ({ priority: String(x.priority), outcome: String(x.outcome ?? '') })),
    sections: e?.sections ?? {},
  };
}

/**
 * Works out every figure for a month, quarter, half or year from the monthly entries.
 * `entries` holds the months of this year (1–12) and, for January's growth, December of last year as month 0.
 */
export function compute(year: number, period: Period, entries: Record<number, Partial<MonthEntry> | undefined>): Calc {
  const p = periodOf(period)!;
  const m = (i: number) => (entries[i] ? cleanMonth(entries[i]) : null);
  const monthCalc = (i: number): MonthCalc | null => {
    const e = m(i); if (!e) return null;
    const prev = m(i - 1);
    const opex = sum(e.expenses.map((x) => x.amount));
    return { month: i, label: MONTHS[i - 1]!, revenue: e.revenue, signups: e.signups, payouts: e.payouts, opex, surplus: e.revenue - opex,
      revenueMoM: prev ? pct(e.revenue, prev.revenue) : null, signupsMoM: prev ? pct(e.signups, prev.signups) : null, expenses: e.expenses };
  };
  const months = p.months.map(monthCalc).filter((x): x is MonthCalc => !!x);
  const missing = p.months.filter((i) => !entries[i]);
  const revenue = sum(months.map((x) => x.revenue)), signups = sum(months.map((x) => x.signups)), payouts = sum(months.map((x) => x.payouts)), opex = sum(months.map((x) => x.opex));
  const first = months[0], last = months[months.length - 1];
  const single = p.months.length === 1;
  // Growth: a month against the month before; a quarter, half or year from its first month to its last.
  const revenueGrowth = single ? (last?.revenueMoM ?? null) : first && last && first !== last ? pct(last.revenue, first.revenue) : null;
  const signupsGrowth = single ? (last?.signupsMoM ?? null) : first && last && first !== last ? pct(last.signups, first.signups) : null;
  const upTo = Math.max(...p.months);
  const ytdMonths = Array.from({ length: upTo }, (_, i) => monthCalc(i + 1)).filter((x): x is MonthCalc => !!x);
  const jan = ytdMonths[0], end = ytdMonths[ytdMonths.length - 1];
  const ytd = { revenue: sum(ytdMonths.map((x) => x.revenue)), signups: sum(ytdMonths.map((x) => x.signups)), payouts: sum(ytdMonths.map((x) => x.payouts)),
    growthSinceJan: jan && end && jan !== end ? pct(end.revenue, jan.revenue) : null, months: ytdMonths.length };
  const by = new Map<string, number>();
  months.forEach((x) => x.expenses.forEach((e) => by.set(e.label, (by.get(e.label) ?? 0) + e.amount)));
  const expensesByLabel = [...by.entries()].map(([label, amount]) => ({ label, amount })).sort((a, b) => b.amount - a.amount);
  const bestMonth = months.length ? months.reduce((a, b) => (b.revenue > a.revenue ? b : a)) : null;
  const surplus = revenue - opex;
  const warnings: string[] = [];
  if (missing.length && !single) warnings.push(`No report yet for ${missing.map((i) => MONTHS[i - 1]).join(', ')}.`);
  for (const x of months) {
    if (x.opex > x.revenue * 3 && x.revenue > 0) warnings.push(`${x.label}: expenses are more than three times revenue. Check the amounts.`);
    if (x.revenue === 0 && x.signups === 0) warnings.push(`${x.label}: revenue and sign-ups are both zero.`);
    if (x.revenueMoM != null && Math.abs(x.revenueMoM) > 300) warnings.push(`${x.label}: revenue changed by ${signed(x.revenueMoM, 0)} from the month before. Check it is right.`);
  }
  const title = single ? `${p.label} ${year}` : `${p.label} ${year}`;
  const tiles: Calc['tiles'] = [
    { label: single ? 'New sign-ups' : 'Sign-ups', value: count(signups), tone: 'gold' },
    { label: single ? `Revenue (${MONTH_SHORT[p.months[0]! - 1]})` : 'Revenue', value: money(revenue), tone: 'dark' },
    { label: single ? 'Revenue MoM' : 'Revenue growth', value: signed(revenueGrowth), tone: (revenueGrowth ?? 0) >= 0 ? 'up' : 'down' },
    { label: surplus >= 0 ? 'Operating surplus' : 'Operating loss', value: `${surplus >= 0 ? '+' : ''}${money(surplus)}`, tone: 'brown' },
    { label: 'Total opex', value: money(opex), tone: 'dark' },
    { label: 'Revenue year to date', value: money(ytd.revenue), tone: 'gold' },
    { label: 'Trader payouts', value: money(payouts), tone: 'dark' },
    { label: 'Growth since Jan', value: signed(ytd.growthSinceJan, 0), tone: 'brown' },
  ];
  return { period, year, label: p.label, title, months, revenue, signups, payouts, opex, surplus, revenueGrowth, signupsGrowth, avgRevenue: months.length ? revenue / months.length : 0, bestMonth, ytd, expensesByLabel, tiles, missing, warnings };
}

/** Text the CEO writes: blank line = new paragraph, "- " = bullet, "a | b | c" lines = table (first line is the header). */
export type Block = { kind: 'p'; text: string } | { kind: 'ul'; items: string[] } | { kind: 'table'; head: string[]; rows: string[][] } | { kind: 'quote'; text: string };
export function blocks(text: string | undefined): Block[] {
  if (!text?.trim()) return [];
  const out: Block[] = [];
  for (const chunk of text.replace(/\r/g, '').split(/\n\s*\n/)) {
    const lines = chunk.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    if (lines.every((l) => l.includes('|'))) { const rows = lines.map((l) => l.replace(/^\||\|$/g, '').split('|').map((c) => c.trim())); out.push({ kind: 'table', head: rows[0]!, rows: rows.slice(1) }); continue; }
    if (lines.every((l) => /^[-•*]\s+/.test(l))) { out.push({ kind: 'ul', items: lines.map((l) => l.replace(/^[-•*]\s+/, '')) }); continue; }
    if (lines[0]!.startsWith('>')) { out.push({ kind: 'quote', text: lines.map((l) => l.replace(/^>\s?/, '')).join(' ') }); continue; }
    out.push({ kind: 'p', text: lines.join(' ') });
  }
  return out;
}
/** **bold** spans. */
export const spans = (t: string) => t.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((s) => (s.startsWith('**') && s.endsWith('**') ? { b: true, t: s.slice(2, -2) } : { b: false, t: s }));
